import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebookF, FaWhatsapp, FaLinkedinIn, FaInstagram,
  FaMapMarkerAlt, FaEnvelope, FaPhone
} from 'react-icons/fa';
import { APP_CONFIG } from './config';

export default function Footer() {
  return (
    <>
      {/* Global CTA Section — Dark Navy Split Banner */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-[2rem] flex flex-col md:flex-row items-stretch min-h-[260px]"
            style={{ background: 'linear-gradient(135deg, #0f1e3d 0%, #1a3268 60%, #1e3a7a 100%)' }}
          >
            {/* Dot-grid overlay */}
            <div className="absolute inset-0 opacity-[0.07]" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }} />

            {/* Left: Text & Buttons */}
            <div className="relative z-10 flex flex-col justify-center px-10 md:px-14 py-10 flex-1 max-w-xl">
              <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">
                Ready to simplify your<br />
                <span className="text-pto-yellow">Canadian payroll?</span>
              </h2>
              <p className="text-blue-200 text-sm font-medium mb-6 max-w-sm leading-relaxed">
                Join thousands of Canadian businesses already trusting Oriental Business Solutions for automated, on-time, and compliant payroll.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <button className="px-7 py-3 btn-pto-yellow shadow-lg text-sm font-extrabold">
                    Book a Free Demo &gt;
                  </button>
                </Link>
                <Link to="/pricing">
                  <button className="px-7 py-3 rounded-full text-sm font-extrabold text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200">
                    View Plans &gt;
                  </button>
                </Link>
              </div>
            </div>

            {/* Right: Clipped Photo Panel */}
            <div className="relative hidden md:block w-[380px] flex-shrink-0">
              {/* Diagonal clip mask */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: 'polygon(12% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
              >
                <img
                  src="/footer.png"
                  alt="Oriental Business Solutions client"
                  className="w-full h-full object-cover object-top"
                />
                {/* subtle dark fade from left to blend with the dark bg */}
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(to right, #1a3268 0%, transparent 35%)'
                }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#3C4859] text-white">
      {/* Top Footer Section */}
      <div className="max-w-7xl mx-auto py-16 px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
        
        {/* Column 1 - Description & Socials */}
        <div className="lg:col-span-3 flex flex-col gap-6">
          <p className="text-sm text-blue-100 leading-relaxed font-medium">
            Automated payroll calculations, direct deposits, CRA source remittances, and ROE processing tailored for Canadian businesses, ensuring compliance and peace of mind.
          </p>
          <div className="flex gap-3">
            <a href={APP_CONFIG.facebookLink} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-blue-800/40 hover:bg-blue-900/60 text-white flex items-center justify-center transition-colors text-base border border-blue-400/20">
              <FaFacebookF />
            </a>
            <a href={APP_CONFIG.whatsAppLink} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-blue-800/40 hover:bg-blue-900/60 text-white flex items-center justify-center transition-colors text-base border border-blue-400/20">
              <FaWhatsapp />
            </a>
            <a href={APP_CONFIG.linkedInLink} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-blue-800/40 hover:bg-blue-900/60 text-white flex items-center justify-center transition-colors text-base border border-blue-400/20">
              <FaLinkedinIn />
            </a>
            <a href={APP_CONFIG.instaLink} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-blue-800/40 hover:bg-blue-900/60 text-white flex items-center justify-center transition-colors text-base border border-blue-400/20">
              <FaInstagram />
            </a>
          </div>
        </div>

        {/* Column 2 - Quick Menu */}
        <div className="lg:col-span-2">
          <h4 className="text-lg font-bold mb-4 tracking-wide text-white">Quick Menu</h4>
          <ul className="space-y-2.5 text-sm text-blue-100 font-semibold">
            <li>
              <Link to="/" className="hover:text-amber-300 transition-colors">Home</Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-amber-300 transition-colors">About Us</Link>
            </li>
            <li>
              <Link to="/products" className="hover:text-amber-300 transition-colors">Services</Link>
            </li>
            <li>
              <Link to="/pricing" className="hover:text-amber-300 transition-colors">Pricing</Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-amber-300 transition-colors">Contact Us</Link>
            </li>
          </ul>
        </div>

        {/* Column 3 - Services */}
        <div className="lg:col-span-2">
          <h4 className="text-lg font-bold mb-4 tracking-wide text-white">Services</h4>
          <ul className="space-y-2.5 text-sm text-blue-100 font-semibold">
            <li className="hover:text-amber-300 transition-colors cursor-pointer">Automated Pay Runs</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">CRA Remittances</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">T4 Year-End Filing</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">ROE Submissions</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">Employee Portal</li>
          </ul>
        </div>

        {/* Column 4 - Quick Links */}
        <div className="lg:col-span-2">
          <h4 className="text-lg font-bold mb-4 tracking-wide text-white">Quick Links</h4>
          <ul className="space-y-2.5 text-sm text-blue-100 font-semibold">
            <li className="hover:text-amber-300 transition-colors cursor-pointer">Canada Pension Plan (CPP)</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">Employment Insurance (EI)</li>
            <li className="hover:text-amber-300 transition-colors cursor-pointer">HST Credit</li>
            
          </ul>
        </div>

        {/* Column 5 - Contact Info */}
        <div className="lg:col-span-3">
          <h4 className="text-lg font-bold mb-4 tracking-wide text-white">Contact</h4>
          <ul className="space-y-4 text-sm text-blue-100 font-semibold">
            <li className="flex items-start gap-3">
              <FaMapMarkerAlt className="text-amber-400 mt-1 flex-shrink-0 text-base" />
              <a href={APP_CONFIG.addressLink} target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">
                {APP_CONFIG.address}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <FaEnvelope className="text-amber-400 flex-shrink-0 text-base" />
              <a href={APP_CONFIG.emailLink} className="hover:text-amber-300 transition-colors">
                {APP_CONFIG.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <FaPhone className="text-amber-400 flex-shrink-0 text-base" />
              <a href={APP_CONFIG.phoneLink} className="hover:text-amber-300 transition-colors">
                {APP_CONFIG.phone}
              </a>
            </li>
          </ul>
        </div>

      </div>

      {/* Bottom Copyright Section */}
      <div className="bg-[#1d2d57] py-6 px-6 text-center text-xs text-blue-200 border-t border-blue-900/40 font-semibold">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p>
            © 2026 Oriental Business Solutions Inc. All rights reserved. | Powered by Magizhini Technology Services Inc.
          </p>
        </div>
      </div>
    </footer>
    </>
  );
}
